import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/layout/Footer';
import { NamedGrayIcon } from '../../components/icons/GrayIconBox';
import './FeatureLanding.css';
import { TEACHER_COUNT_LABEL } from '../../constants/siteStats';
import { WorksheetResultDemo } from '../../components/demo';

const AiWorksheetLandingPage = () => {
  const navigate = useNavigate();

  const faqData = [
    {
      q: '학습지를 한글 파일로 받을 수 있나요?',
      a: '네, 글자형 학습지는 한글(HWPX)·워드·PDF로 받아요. 디자인형 학습지(PRO)는 그림처럼 꾸민 학습지로 받아요. 정답지도 함께 만들어요.'
    },
    {
      q: '어떤 유형의 문제를 만들 수 있나요?',
      a: 'OX 퀴즈, 단답형, 서술형, 빈칸 채우기, 선택형 등을 만들 수 있어요. 원하는 유형과 문항 수를 적으면 그대로 만들어요.'
    },
    {
      q: '교육과정에 맞는 문제가 나오나요?',
      a: '네, 왓퀴즈 AI는 한국 교육과정을 기반으로 학년/과목별 맞춤 문제를 생성해요. PRO는 교과서 PDF를 첨부해 그 단원 내용으로 학습지를 만들 수 있어요.'
    },
    {
      q: '난이도 조절이 가능한가요?',
      a: '네, 처음에 난이도를 적거나 만든 뒤 "난이도 높여 줘", "3번 서술형으로 바꿔 줘"처럼 말하면 그대로 고쳐요.'
    },
    {
      q: '수행평가용 학습지도 만들 수 있나요?',
      a: '네, 수행평가 학습지도 만들 수 있어요. 학생 답안은 왓퀴즈 서술형 채점에서 기준표대로 채점할 수 있어요.'
    },
    {
      q: '무료로 사용할 수 있나요?',
      a: '네, 가입만 하면 글자형 학습지를 한 번에 10문항까지 만들어 한글·워드·PDF로 받을 수 있어요. 디자인형 학습지와 250문항까지 만들기는 PRO 기능이에요.'
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        "name": "왓퀴즈 AI 학습지 만들기",
        "applicationCategory": "EducationalApplication",
        "operatingSystem": "Web",
        "url": "https://whatquiz.co.kr/ai-worksheet",
        "description": "교사 전용 AI 학습지 자동 생성 도구. 과목과 주제만 입력하면 OX, 단답형, 서술형 등 다양한 유형의 학습지가 자동 생성돼요.",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "KRW",
          "description": "무료로 시작, 디자인형 학습지는 PRO"
        },
        "creator": {
          "@type": "Organization",
          "name": "왓퀴즈",
          "url": "https://whatquiz.co.kr"
        }
      },
      {
        "@type": "HowTo",
        "name": "AI로 학습지 만드는 방법",
        "description": "왓퀴즈에서 AI로 학습지를 자동 생성하는 3단계 방법",
        "step": [
          {
            "@type": "HowToStep",
            "position": 1,
            "name": "과목·주제 입력",
            "text": "과목, 학년, 단원, 문항 수를 적어요. PRO는 교과서 PDF를 첨부해 그 내용으로 만들 수 있어요."
          },
          {
            "@type": "HowToStep",
            "position": 2,
            "name": "확인하고 고치기",
            "text": "만든 학습지에서 글자를 바로 고치거나 \"3번 서술형으로 바꿔 줘\"처럼 말해요."
          },
          {
            "@type": "HowToStep",
            "position": 3,
            "name": "한글·워드·PDF로 받기",
            "text": "한글·워드·PDF로 받아 바로 인쇄해요. 정답지도 함께 나와요."
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a
          }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "왓퀴즈", "item": "https://whatquiz.co.kr" },
          { "@type": "ListItem", "position": 2, "name": "AI 학습지 만들기", "item": "https://whatquiz.co.kr/ai-worksheet" }
        ]
      }
    ]
  };

  return (
    <div className="feature-landing">
      <Helmet>
        <title>AI 학습지 만들기 | 시험문제·퀴즈 자동 생성 | 교사 전용 무료 | 왓퀴즈</title>
        <meta name="description" content={`과목과 주제만 입력하면 AI가 OX, 단답형, 서술형 학습지를 자동 생성해요. 정답지까지 함께, 한글·워드·PDF로 받기. ${TEACHER_COUNT_LABEL} 교사가 검증한 왓퀴즈에서 무료로 시작하세요.`} />
        <meta name="keywords" content="학습지 만들기, 학습지 만들기 사이트, AI 학습지 만들기, 시험문제 만들기, 퀴즈 만들기, 수행평가 학습지, AI 문제 출제, 학습지 자동 생성, 교사 학습지, 무료 학습지 만들기" />
        <link rel="canonical" href="https://whatquiz.co.kr/ai-worksheet" />
        <meta name="robots" content="index, follow" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://whatquiz.co.kr/ai-worksheet" />
        <meta property="og:title" content="AI 학습지 만들기 | 시험문제 자동 생성 | 왓퀴즈" />
        <meta property="og:description" content="과목과 주제만 입력하면 AI가 학습지를 자동 생성. OX, 서술형, 빈칸 채우기 등. 교사 전용 무료." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png" />
        <meta property="og:locale" content="ko_KR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI 학습지 만들기 | 시험문제 자동 생성 | 왓퀴즈" />
        <meta name="twitter:description" content="과목과 주제만 입력하면 AI가 학습지를 자동 생성. 교사 전용 무료." />

        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* 히어로 */}
      <section className="feature-hero" style={{ background: 'linear-gradient(180deg, #eff6ff 0%, #fff 100%)' }}>
        <div className="feature-hero-inner">
          <span className="feature-hero-badge" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
            교사 전용 AI
          </span>
          <h1>주제만 적으면<br /><em>학습지와 정답지</em>까지</h1>
          <p className="feature-hero-desc">
            적은 문항 수와 유형대로 정답지까지 만들어요.<br />
            한글·워드·PDF로 받아 바로 인쇄해요.
          </p>
          <button
            className="feature-hero-cta"
            onClick={() => navigate('/chat?category=worksheet_gen')}
          >
            무료로 학습지 만들기 →
          </button>
        </div>
      </section>

      <section className="feature-section feature-section--demo">
        <div className="feature-section-inner feature-demo">
          <WorksheetResultDemo surface="ai_worksheet" title="AI 학습지" />
        </div>
      </section>

      {/* 3단계 사용법 */}
      <section className="feature-section feature-section--gray">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">학습지를 어떻게 만드나요?</h2>
          <p className="feature-section-subtitle">3단계로 수업용 학습지가 완성돼요</p>
          <div className="feature-steps">
            <div className="feature-step">
              <div className="feature-step-number" style={{ background: '#3182f6' }}>1</div>
              <h3>과목·주제 입력</h3>
              <p>"중1 과학 광합성 10문항, 객관식"처럼 적어요. PRO는 교과서 PDF를 첨부할 수 있어요.</p>
            </div>
            <div className="feature-step">
              <div className="feature-step-number" style={{ background: '#3182f6' }}>2</div>
              <h3>확인하고 고치기</h3>
              <p>만든 학습지에서 글자를 바로 고치거나 "3번 서술형으로 바꿔 줘"처럼 말해요.</p>
            </div>
            <div className="feature-step">
              <div className="feature-step-number" style={{ background: '#3182f6' }}>3</div>
              <h3>한글·워드·PDF로 받기</h3>
              <p>받아서 바로 인쇄해요. 정답지도 함께 나와요.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 특징 */}
      <section className="feature-section">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">왓퀴즈 AI 학습지, 뭐가 다를까요?</h2>
          <p className="feature-section-subtitle">교과과정을 아는 AI가 만드는 학습지예요</p>
          <div className="feature-grid">
            <div className="feature-card">
              <div className="feature-card-icon"><NamedGrayIcon name="book" /></div>
              <h3>교과과정 기반 출제</h3>
              <p>한국 교육과정을 바탕으로 학년·과목에 맞는 문제를 만들어요. PRO는 교과서 PDF를 첨부할 수 있어요.</p>
            </div>
            <div className="feature-card">
              <div className="feature-card-icon"><NamedGrayIcon name="note" /></div>
              <h3>다양한 문제 유형</h3>
              <p>OX 퀴즈, 단답형, 서술형, 빈칸 채우기, 선택형을 섞어 만들 수 있어요.</p>
            </div>
            <div className="feature-card">
              <div className="feature-card-icon"><NamedGrayIcon name="chart" /></div>
              <h3>서술형 채점과 이어 쓰기</h3>
              <p>수행평가 학습지로 받은 학생 답안은 왓퀴즈 서술형 채점에서 기준표대로 채점해요.</p>
            </div>
            <div className="feature-card">
              <div className="feature-card-icon"><NamedGrayIcon name="printer" /></div>
              <h3>한글 파일로 받기</h3>
              <p>한글·워드·PDF로 받아 바로 인쇄하거나 학교 양식에 맞춰 고쳐요.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 비교표 */}
      <section className="feature-section feature-section--gray">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">학습지 만들기 도구 비교</h2>
          <div className="feature-compare">
            <table>
              <thead>
                <tr>
                  <th>비교 항목</th>
                  <th>수작업</th>
                  <th>ChatGPT</th>
                  <th className="highlight-col">왓퀴즈</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>교과과정 반영</strong></td>
                  <td>직접 확인</td>
                  <td>프롬프트에 의존</td>
                  <td className="highlight-col">자동 반영</td>
                </tr>
                <tr>
                  <td><strong>다운로드 형식</strong></td>
                  <td>직접 작성</td>
                  <td>복사해 붙여 넣기</td>
                  <td className="highlight-col">한글·워드·PDF</td>
                </tr>
                <tr>
                  <td><strong>채점 연계</strong></td>
                  <td>없음</td>
                  <td>없음</td>
                  <td className="highlight-col">서술형 채점과 이어 쓰기</td>
                </tr>
                <tr>
                  <td><strong>생기부 연계</strong></td>
                  <td>없음</td>
                  <td>없음</td>
                  <td className="highlight-col">올인원 지원</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="feature-section">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">자주 묻는 질문</h2>
          <div className="feature-faq-list">
            {faqData.map((faq, idx) => (
              <div key={idx} className="feature-faq-item">
                <h3 className="feature-faq-q">{faq.q}</h3>
                <p className="feature-faq-a">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="feature-bottom-cta">
        <h2>다음 단원평가,<br />주제 한 줄로 시작해 보세요</h2>
        <p>가입만 하면 무료로 시작할 수 있어요</p>
        <div className="feature-bottom-cta-buttons">
          <button className="feature-cta-primary" onClick={() => navigate('/chat?category=worksheet_gen')}>
            무료로 학습지 만들기
          </button>
          <button className="feature-cta-secondary" onClick={() => navigate('/pricing')}>
            요금제 보기
          </button>
        </div>
      </section>

      {/* 관련 기능 */}
      <section className="feature-related">
        <div className="feature-related-inner">
          <h2>이런 기능도 있어요</h2>
          <div className="feature-related-grid">
            <div className="feature-related-card" onClick={() => navigate('/ai-ppt')}>
              <h3>AI PPT 만들기</h3>
              <p>주제만 적으면 수업 PPT, 글자도 바로 고쳐요</p>
            </div>
            <div className="feature-related-card" onClick={() => navigate('/ai-image')}>
              <h3>AI 이미지 생성</h3>
              <p>색칠공부, 포스터, 플래시카드 등 교육용 이미지 생성</p>
            </div>
            <div className="feature-related-card" onClick={() => navigate('/scoring')}>
              <h3>AI 수행평가 채점</h3>
              <p>기준표대로 반 전체 서술형 답안 채점</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AiWorksheetLandingPage;
